/**
 * First-login (set-password) page (Phase 4, 2026-05-22).
 *
 * Reached via the magic-link email from Discovery's user-invitation
 * flow. URL shape:
 *
 *   https://<slug>.discovery.foctta.com/first-login#t=<rawToken>
 *
 * The token lives in the URL fragment (not query) - never sent to
 * server-side logs, doesn't appear in referrer headers, stripped from
 * browser history on this page's mount.
 *
 * Flow:
 *   1. Mount → parse token from fragment → wipe fragment from URL
 *   2. Call GET /v1/auth/verify-magic-link?t=<raw> - reveals
 *      user.email + user.fullName + user.role for pre-fill, no
 *      consumption yet
 *   3. User picks a password (with strength meter)
 *   4. POST /v1/auth/set-password → atomic token-consume + activate
 *      + mint session, or a partial-auth token when the account has MFA
 *      enrolled or required (decision 65)
 *   5. setAuth + hard navigate to /welcome, or on to the MFA page
 *      (mfa-handoff.ts) with the partial-auth token in the fragment
 *
 * Used for BOTH first_login (initial invitation) and forgot_password
 * (purpose returned from verify endpoint differentiates the headline).
 */

import { useEffect, useMemo, useState } from 'react';
import { Loader2, ShieldAlert, CheckCircle2 } from 'lucide-react';

import { Mark } from '@/components/brand/Mark';
import {
  setAuth,
  setPasswordApi,
  tenantApexFromHost,
  verifyMagicLinkApi,
} from '@/lib/api';
import type { MagicLinkVerification } from '@/lib/api';
import { mfaHandoffTarget, mfaNavigation } from './mfa-handoff';

type State =
  | { stage: 'verifying' }
  | { stage: 'set_password'; verification: MagicLinkVerification; token: string }
  | { stage: 'submitting'; verification: MagicLinkVerification; token: string }
  | { stage: 'success' }
  | { stage: 'error'; title: string; detail: string };

function parseFragment(hash: string): string | null {
  const trimmed = hash.startsWith('#') ? hash.slice(1) : hash;
  if (!trimmed) return null;
  const params = new URLSearchParams(trimmed);
  return params.get('t');
}

interface PasswordStrength {
  score: 0 | 1 | 2 | 3 | 4;
  label: string;
  color: string;
}

function rateStrength(password: string): PasswordStrength {
  if (password.length === 0) return { score: 0, label: '', color: '' };
  let score = 0;
  if (password.length >= 12) score++;
  if (password.length >= 16) score++;
  if (/[a-z]/.test(password) && /[A-Z]/.test(password)) score++;
  if (/\d/.test(password) && /[^A-Za-z0-9]/.test(password)) score++;
  const scoreCapped = Math.min(score, 4) as 0 | 1 | 2 | 3 | 4;
  return [
    { score: 0 as const, label: '', color: '' },
    { score: 1 as const, label: 'Too short', color: 'bg-red-500' },
    { score: 2 as const, label: 'Weak', color: 'bg-orange-500' },
    { score: 3 as const, label: 'OK', color: 'bg-yellow-500' },
    { score: 4 as const, label: 'Strong', color: 'bg-emerald-500' },
  ][scoreCapped]!;
}

export function FirstLogin() {
  const [state, setState] = useState<State>({ stage: 'verifying' });
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');

  useEffect(() => {
    const token = parseFragment(window.location.hash);
    // Wipe the fragment for safety - same defence as /auth/bridge
    if (window.location.hash) {
      const cleanUrl = window.location.pathname + window.location.search;
      window.history.replaceState(null, '', cleanUrl);
    }
    if (!token) {
      setState({
        stage: 'error',
        title: 'Sign-in link incomplete',
        detail: "This link is missing its token. It may have already been used or stripped by your email client. Ask your admin for a new invitation.",
      });
      return;
    }

    let cancelled = false;
    verifyMagicLinkApi(token)
      .then((verification) => {
        if (cancelled) return;
        setState({ stage: 'set_password', verification, token });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const detail =
          err instanceof Error
            ? err.message
            : 'We could not verify this sign-in link.';
        setState({ stage: 'error', title: 'Sign-in link invalid', detail });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const strength = useMemo(() => rateStrength(password), [password]);
  const matches = password.length > 0 && password === confirm;
  const passwordValid = password.length >= 12 && matches;

  const onSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (state.stage !== 'set_password') return;
    if (!passwordValid) return;
    setState({ ...state, stage: 'submitting' });
    try {
      const result = await setPasswordApi({
        magicLinkToken: state.token,
        password,
      });
      const tenantSlug = state.verification.tenant.slug;
      // The link set the password; it is not a second factor (decision 65).
      // An account with MFA enrolled or required continues to the MFA page
      // exactly as a password sign-in does, with the partial-auth token in the
      // fragment; nothing is stored here.
      if ('mfaChallengeRequired' in result && result.mfaChallengeRequired) {
        const origin = tenantSlug ? `https://${tenantSlug}.${tenantApexFromHost()}` : '';
        mfaNavigation.replace(mfaHandoffTarget(result.mfaReason, result.partialAuth.token, origin));
        return;
      }
      setAuth(result.session.token, result.user, result.session.expiresAt);
      setState({ stage: 'success' });
      // 2026-05-27 - land on Home (/) post-first-login. Welcome is
      // a settings-area checklist now, not the landing page.
      const target = tenantSlug
        ? `https://${tenantSlug}.${tenantApexFromHost()}/`
        : '/';
      setTimeout(() => window.location.replace(target), 400);
    } catch (err) {
      setState({
        stage: 'error',
        title: 'Could not set password',
        detail: err instanceof Error ? err.message : 'Unknown error.',
      });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950 py-12 px-4 flex items-center justify-center">
      <div className="w-full max-w-md">
        <div className="flex flex-col items-center gap-3 mb-8">
          <Mark variant="wordmark" size={44} />

          {state.stage === 'verifying' ? (
            <>
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
              <p className="text-sm text-muted-foreground">Verifying your sign-in link…</p>
            </>
          ) : null}

          {(state.stage === 'set_password' || state.stage === 'submitting') ? (
            <>
              <h1 className="text-2xl font-bold tracking-tight">
                {state.verification.purpose === 'forgot_password'
                  ? 'Reset your password'
                  : state.verification.user.isFirstLogin
                    ? 'Activate your account'
                    : 'Set a new password'}
              </h1>
              <p className="text-sm text-muted-foreground">
                {state.verification.user.email}
              </p>
            </>
          ) : null}

          {state.stage === 'success' ? (
            <>
              <CheckCircle2 className="h-10 w-10 text-emerald-500" />
              <p className="text-base font-semibold text-emerald-700 dark:text-emerald-300">
                You're signed in.
              </p>
              <p className="text-sm text-muted-foreground">Loading your workspace…</p>
            </>
          ) : null}

          {state.stage === 'error' ? (
            <>
              <ShieldAlert className="h-10 w-10 text-amber-500" />
              <h1 className="text-lg font-semibold">{state.title}</h1>
              <p className="text-sm text-muted-foreground text-center">{state.detail}</p>
              <a
                href="/forgot-password"
                className="mt-2 inline-block text-sm text-primary hover:underline"
              >
                Request a new sign-in link
              </a>
            </>
          ) : null}
        </div>

        {state.stage === 'set_password' || state.stage === 'submitting' ? (
          <form
            onSubmit={onSubmit}
            className="space-y-5 bg-white dark:bg-slate-900 rounded-lg shadow-sm border p-8"
          >
            <div>
              <label htmlFor="password" className="block text-sm font-medium mb-1">
                Choose a password
              </label>
              <input
                id="password"
                type="password"
                required
                minLength={12}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-primary"
                autoComplete="new-password"
                autoFocus
              />
              {password.length > 0 ? (
                <div className="mt-2">
                  <div className="h-1.5 w-full bg-slate-200 dark:bg-slate-800 rounded-full overflow-hidden">
                    <div
                      className={`h-full transition-all ${strength.color}`}
                      style={{ width: `${(strength.score / 4) * 100}%` }}
                    />
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {strength.label || 'At least 12 characters'}
                  </p>
                </div>
              ) : (
                <p className="mt-1 text-xs text-muted-foreground">
                  At least 12 characters. Mix upper/lower case + digits + symbols for the strongest score.
                </p>
              )}
            </div>

            <div>
              <label htmlFor="confirm" className="block text-sm font-medium mb-1">
                Confirm password
              </label>
              <input
                id="confirm"
                type="password"
                required
                minLength={12}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-primary"
                autoComplete="new-password"
              />
              {confirm.length > 0 && !matches ? (
                <p className="mt-1 text-xs text-red-600">Passwords don't match.</p>
              ) : null}
            </div>

            <button
              type="submit"
              disabled={state.stage === 'submitting' || !passwordValid}
              className="w-full inline-flex justify-center items-center gap-2 rounded-md bg-primary px-4 py-3 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {state.stage === 'submitting' ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Saving…
                </>
              ) : (
                'Set password and sign in'
              )}
            </button>

            <p className="text-xs text-muted-foreground text-center pt-2">
              Signed in as <span className="font-mono">{state.verification.user.email}</span> ·{' '}
              <span className="capitalize">{state.verification.user.role.replace(/_/g, ' ')}</span>
            </p>
          </form>
        ) : null}
      </div>
    </div>
  );
}

export default FirstLogin;
